/**
 * The `before_send` chain of the profiles (spec D):
 *   impersonation gate → hostGuard (landing) → contextMerge → URL sanitiser →
 *   scrubPii → fingerprintKnown → sampleWebVitals (full) → dropRoutes (studio)
 *
 * posthog-js runs `before_send` synchronously and nothing above it catches, so
 * every step here must never throw: a step that fails keeps the event as it is.
 */

import type { BeforeSendFn, CaptureResult } from 'posthog-js';

import { HOSTS } from './registry/hosts';
import { scrubProperties } from './redact';
import { sanitizeUrlProperties } from './url';

type Props = Record<string, unknown>;

function safe(step: (cr: CaptureResult) => CaptureResult | null): BeforeSendFn {
  return (cr) => {
    if (!cr) return null;
    try {
      return step(cr);
    } catch {
      return cr;
    }
  };
}

/**
 * Drops every event while a support member impersonates a merchant, or while
 * the session is not known yet (spec B). `onBlocked` hears each dropped name,
 * `onPassed` each name let through.
 */
export function impersonationGate(
  isImpersonated: () => boolean,
  onBlocked?: (event: string) => void,
  onPassed?: (event: string) => void,
): BeforeSendFn {
  return (cr) => {
    if (!cr) return null;
    try {
      if (!isImpersonated()) {
        onPassed?.(cr.event);
        return cr;
      }
      onBlocked?.(cr.event);
      return null;
    } catch {
      return null;
    }
  };
}

const LANDING_PREVIEW = /^tagadapay-landing-v3-[a-z0-9-]+\.vercel\.app$/;

export function isLandingHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return host === 'localhost' || HOSTS.landing.includes(host) || LANDING_PREVIEW.test(host);
}

/** Landing only: copies of the landing HTML on another host never write into Main. */
export function hostGuard(hostname: () => string): BeforeSendFn {
  return (cr) => {
    if (!cr) return null;
    try {
      return isLandingHost(hostname()) ? cr : null;
    } catch {
      return null;
    }
  };
}

/** Merges the app's dynamic context (A4) under the event's own props; `$groups` are merged key by key. */
export function contextMerge(getContext: () => Props | undefined): BeforeSendFn {
  return safe((cr) => {
    const ctx = getContext();
    if (!ctx) return cr;
    const { $groups: ctxGroups, ...rest } = ctx;
    const props: Props = { ...rest, ...cr.properties };
    if (ctxGroups && typeof ctxGroups === 'object') {
      const own = cr.properties['$groups'];
      props['$groups'] = { ...(ctxGroups as Props), ...(own && typeof own === 'object' ? (own as Props) : {}) };
    }
    return { ...cr, properties: props };
  });
}

export function sanitizeUrls(keepParams: readonly string[]): BeforeSendFn {
  return safe((cr) => {
    const out: CaptureResult = { ...cr, properties: sanitizeUrlProperties(cr.properties, keepParams) };
    if (cr.$set) out.$set = sanitizeUrlProperties(cr.$set, keepParams);
    if (cr.$set_once) out.$set_once = sanitizeUrlProperties(cr.$set_once, keepParams);
    return out;
  });
}

export const scrubPii: BeforeSendFn = safe((cr) => {
  const out: CaptureResult = { ...cr, properties: scrubProperties(cr.properties) };
  if (cr.$set) out.$set = (scrubProperties({ $set: cr.$set })['$set'] as Props | undefined) ?? cr.$set;
  if (cr.$set_once) {
    out.$set_once = (scrubProperties({ $set_once: cr.$set_once })['$set_once'] as Props | undefined) ?? cr.$set_once;
  }
  return out;
});

const CHUNK_LOAD = /ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed|'text\/html' is not a valid JavaScript MIME type/;
const EXTENSION_FRAME = /^(chrome-extension|moz-extension):\/\//;

interface ExceptionEntry {
  type?: unknown;
  value?: unknown;
  stacktrace?: { frames?: { filename?: unknown; abs_path?: unknown }[] };
}

function frameUrl(frame: { filename?: unknown; abs_path?: unknown }): string {
  const v = frame.abs_path ?? frame.filename;
  return typeof v === 'string' ? v : '';
}

/** Groups chunk-load failures under one fingerprint; drops errors thrown only by browser extensions. */
export const fingerprintKnown: BeforeSendFn = safe((cr) => {
  if (cr.event !== '$exception') return cr;
  const list = cr.properties['$exception_list'];
  if (!Array.isArray(list) || list.length === 0) return cr;
  const entries = list as ExceptionEntry[];
  const frames = entries.flatMap((e) => e.stacktrace?.frames ?? []);
  if (frames.length > 0 && frames.every((f) => EXTENSION_FRAME.test(frameUrl(f)))) return null;
  const isChunkLoad = entries.some((e) => CHUNK_LOAD.test(`${String(e.type ?? '')} ${String(e.value ?? '')}`));
  if (!isChunkLoad) return cr;
  return { ...cr, properties: { ...cr.properties, $exception_fingerprint: 'chunk_load_error' } };
});

/** FNV-1a 32-bit: a stable, cheap hash of the session id. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Keeps `$web_vitals` for one session in four (`hash($session_id) % 4 === 0`) and stamps `sample_rate`. */
export function sampleWebVitals(rate = 0.25): BeforeSendFn {
  const modulo = Math.round(1 / rate);
  return safe((cr) => {
    if (cr.event !== '$web_vitals') return cr;
    const sessionId = cr.properties['$session_id'];
    if (typeof sessionId !== 'string' || hashString(sessionId) % modulo !== 0) return null;
    return { ...cr, properties: { ...cr.properties, sample_rate: rate } };
  });
}

/** Drops every event of the routes that must never be tracked (studio `/test /thumb /capture /sso-callback`). */
export function dropRoutes(pattern: RegExp, pathname: () => string): BeforeSendFn {
  return safe((cr) => {
    const fromEvent = cr.properties['$pathname'];
    const path = typeof fromEvent === 'string' ? fromEvent : pathname();
    return pattern.test(path) ? null : cr;
  });
}

export const STUDIO_DROPPED_ROUTES = /^\/(test|thumb|capture|sso-callback)(\/|$)/;
